import { useAuth } from '@/contexts/AuthContext';
import { profileService } from '@/lib/services/profile/ProfileService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const initUser = {
  created_at: '',
  display_name: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  email: '',
  id: '',
  image_url: null as string | null,
  is_internal: false,
  phone_number: null as string | null,
  onboarding_completed: null,
  updated_at: null,
  metadata: null,
};

export function useProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const profileQuery = useQuery({
    queryKey: ['user', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return initUser;

      const { profile } = await profileService.getMe();
      return profile || initUser;
    },
  });

  const updateProfile = useMutation({
    mutationFn: async (updatedProfile: Partial<typeof initUser>) => {
      const {
        id,
        display_name,
        first_name,
        middle_name,
        last_name,
        phone_number,
        image_url,
        onboarding_completed,
      } = updatedProfile;
      if (!id) {
        throw new Error('Profile ID is required');
      }

      return profileService.updateProfile({
        display_name,
        first_name,
        middle_name,
        last_name,
        phone_number,
        image_url,
        onboarding_completed,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['user', user?.id], data);
    },
  });

  const updatePreferredLanguage = useMutation({
    mutationFn: (language: string) =>
      profileService.updateProfile({ preferred_language: language }),
  });

  const updateOnboardingStatus = useMutation({
    mutationFn: async (completed: boolean) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      return profileService.updateProfile({
        onboarding_completed: completed,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['user', user?.id], data);
    },
  });

  const uploadProfilePhoto = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Upload via API endpoint
      const photoUrl = await profileService.uploadPhoto(file);

      return photoUrl;
    },
    onSuccess: (photoUrl) => {
      // Optimistically update the cache
      queryClient.setQueryData(
        ['user', user?.id],
        (old: typeof initUser | undefined) => {
          if (!old) return old;
          return {
            ...old,
            image_url: photoUrl,
          };
        }
      );

      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['user', user?.id] });
    },
  });

  const updateMetadata = useMutation({
    scope: { id: 'profileMetadata' },
    mutationFn: (patch: Record<string, unknown>) =>
      profileService.updateMetadata(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({
        queryKey: ['user', user?.id, 'metadata'],
      });
      queryClient.setQueryData(
        ['user', user?.id, 'metadata'],
        (old: Record<string, unknown> | undefined) => ({ ...old, ...patch })
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['user', user?.id, 'metadata'],
      });
    },
  });

  return {
    // Existing exports
    profile: profileQuery.data,
    isFetchingProfile: profileQuery.isFetching,
    isUpdatingProfile: updateProfile.isPending,
    refetchProfile: profileQuery.refetch,
    updateProfile: updateProfile.mutateAsync,
    updatePreferredLanguage: updatePreferredLanguage.mutateAsync,
    updateOnboardingStatus: updateOnboardingStatus.mutateAsync,
    isUpdatingOnboardingStatus: updateOnboardingStatus.isPending,

    // New photo upload exports
    uploadProfilePhoto: uploadProfilePhoto.mutateAsync,
    isUploadingPhoto: uploadProfilePhoto.isPending,
    updateMetadata: updateMetadata.mutate,
  };
}

export function useProfileMetadata() {
  const { user } = useAuth();

  const metadataQuery = useQuery({
    queryKey: ['user', user?.id, 'metadata'],
    enabled: !!user?.id,
    queryFn: () => profileService.getMetadata(),
  });

  return {
    metadata: metadataQuery.data,
  };
}
